import { io } from 'socket.io-client';
import config from '../config.js';

/**
 * Shared Socket.IO client for the creative-edit chat relay.
 *
 * This is a pure relay, not a data store: the order-booking API persists
 * every message over REST first, and the hub only fans a copy of that
 * already-persisted message out to the *other* sockets sitting in the same
 * `creative_<bscId>` room (`socket.to(room)` — never back to the sender).
 * There is no auth handshake and nothing here writes to a database; if the
 * hub is unreachable the three chat UIs simply fall back to their existing
 * refetch-on-open behaviour.
 *
 * One connection is shared for the whole app (lazily opened on first use,
 * see `getSocket`) — never open a socket per component mount, and never
 * disconnect it just because one thread panel closed; only
 * `disconnectCreativeChat` tears it down.
 *
 * Field-shape mismatch between the two sides, bridged by `fromHubLog` /
 * `toHubLog`:
 *  - hub / mobile `/logs` shape: camelCase keys, epoch-seconds timestamp
 *    under `at` — `{ id, userId, userName, userType, message, activityType,
 *    mediaUrl, mediaType, replyToId, changes, at }`.
 *  - this panel's shape: snake_case keys, epoch-seconds timestamp under
 *    `created_time` — `{ id, user_id, message, user_name, user_type,
 *    activity_type, media_url, media_type, created_time, changes }`.
 *    `reply_to_id` isn't read by any of the three chat systems today, but
 *    the column exists server-side, so it is still passed through (with a
 *    safe fallback) for parity. `userId`/`changes` ARE forwarded — a
 *    relayed message missing `userId` broke the "is this mine" check on
 *    receivers that key off it instead of `userType`, and a relayed
 *    field-edit/status row missing `changes` rendered with no diff.
 */

// hub (mobile /logs) shape -> this panel's snake_case shape
function fromHubLog(hubLog) {
  if (!hubLog) return null;
  return {
    id: hubLog.id,
    user_id: hubLog.userId ?? null,
    message: hubLog.message ?? '',
    user_name: hubLog.userName,
    user_type: hubLog.userType,
    activity_type: hubLog.activityType || 'message',
    media_url: hubLog.mediaUrl || null,
    media_type: hubLog.mediaType || null,
    reply_to_id: hubLog.replyToId ?? null,
    changes: hubLog.changes ?? null,
    created_time: hubLog.at,
  };
}

// this panel's snake_case shape -> hub (mobile /logs) shape
function toHubLog(panelLog) {
  if (!panelLog) return null;
  return {
    id: panelLog.id,
    userId: panelLog.user_id ?? null,
    message: panelLog.message ?? '',
    userName: panelLog.user_name,
    userType: panelLog.user_type,
    activityType: panelLog.activity_type || 'message',
    mediaUrl: panelLog.media_url || null,
    mediaType: panelLog.media_type || null,
    replyToId: panelLog.reply_to_id ?? null,
    changes: panelLog.changes ?? null,
    at: panelLog.created_time,
  };
}

let socket = null;

// Returns null (no connection attempted) when the hub isn't configured —
// mirrors the order-booking API's own SOCKET_ENABLED=false fallback: chat
// simply stays on refetch-on-open, no noisy reconnect loop against an empty URL.
function getSocket() {
  if (!config.ORDER_BOOKING_SOCKET_URL) return null;
  if (socket) return socket;
  socket = io(config.ORDER_BOOKING_SOCKET_URL, {
    path: config.ORDER_BOOKING_SOCKET_PATH,
    transports: ['websocket', 'polling'],
    reconnection: true,
  });
  return socket;
}

/**
 * Join the `creative_<bscId>` room and start relaying live activity into the
 * caller's callbacks. Returns a cleanup function that leaves the room and
 * removes the listeners — call it on unmount / when the thread closes.
 *
 * `onDelete` support is wired through for parity with the hub protocol; none
 * of the three panel chat systems has a delete action today, so nothing
 * currently calls it, but the plumbing is here so a future one can.
 */
export function openCreativeChat(bscId, { onMessage, onDelete } = {}) {
  const s = getSocket();
  if (!s || !bscId) return () => {};

  const join = () => s.emit('joinCreative', { bscId });
  if (s.connected) join();
  s.on('connect', join);

  const handleMessage = (payload) => {
    const log = fromHubLog(payload?.log);
    if (log && onMessage) onMessage(log);
  };
  const handleDelete = (payload) => {
    const logId = payload?.logId ?? payload?.log_id;
    if (logId != null && onDelete) onDelete(logId);
  };
  s.on('logMessageUpdated', handleMessage);
  s.on('logMessageDeleted', handleDelete);

  return () => {
    s.emit('leaveCreative', { bscId });
    s.off('connect', join);
    s.off('logMessageUpdated', handleMessage);
    s.off('logMessageDeleted', handleDelete);
  };
}

/**
 * Broadcast a message that REST has already persisted. Call this AFTER the
 * REST response lands — the hub does not echo back to the sender's own
 * socket, so this can never cause the sender to see its own message twice.
 */
export function relayCreativeMessage(bscId, log) {
  const s = getSocket();
  if (!s || !bscId || !log) return;
  s.emit('sendMessage', { bscId, log: toHubLog(log) });
}

/** Tear down the shared connection entirely (e.g. on logout). */
export function disconnectCreativeChat() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}
